import { computed, unref } from 'vue'
import { createMultipleListQuery, hasAll, toUpperCase } from '../utils'
import { createSharedComposable } from '@vueuse/core'
import { useCountries } from '@/composables/useCountries'
import { useCountry } from './country'

export const useCompareCountries = createSharedComposable(() => {
    const { countries, codes, byRegion } = useCountries()
    const [country] = useCountry()

    const countriesList = computed(() => {
        return unref(countries).map(({ name, iso, region }) => ({
            label: name,
            value: iso,
            region: region
        }))
    })

    const list = computed(() => {
        return unref(countriesList).filter(({ value }) => {
            return value !== unref(country)
        })
    })

    return createMultipleListQuery('compare-countries', list, {
        normalize: toUpperCase,
        get: value => {
            if (!value) return
            const values = toUpperCase(value.split('-'))
            const result = replaceRegionsWithCountries(unref(byRegion), values)
            if (hasAll(result, unref(codes))) return

            return result
        },
        set: values => {
            if (!values || hasAll(values, unref(codes))) return

            return replaceCountriesWithRegions(unref(byRegion), values).join(
                '-'
            )
        },
        defaultValue: undefined
    })
})

/**
 * Replace countries in the lsit ot their
 * region name if all of them are present.
 */
function replaceCountriesWithRegions(
    regions: Map<Region['id'], Country['iso'][]>,
    keys: string[]
) {
    const list = new Set(keys)

    for (const [region, countries] of regions.entries()) {
        if (countries.every(country => list.has(country))) {
            countries.forEach(country => list.delete(country))
            list.add(String(region))
        }
    }

    return [...list.values()]
}

/**
 * Replace all regions by their list of countries.
 */
function replaceRegionsWithCountries(
    regions: Map<Region['id'], Country['iso'][]>,
    keys: string[]
) {
    return keys.flatMap(value => {
        const countries = regions.get(Number(value))

        if (countries) {
            return countries
        }

        return value
    })
}
